import {
  AUTOCOMPLETE_HISTORY,
  ISSUE_TYPES,
  LIFESTYLE_FACTORS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { HISTORY_TYPES } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';

export type FieldType = 'text' | 'autocomplete' | 'select' | 'date';

export interface FieldConfig {
  name: string;
  label?: string;
  type: FieldType;
  options?: any[];
  style?: React.CSSProperties;
  required?: boolean;
  dynamicProps?: (watchedValues: Record<string, any>) => {
    disabled?: boolean;
    label?: string;
  };
}

export interface SectionConfig {
  title: string;
  formKey: string;
  fields: FieldConfig[];
  presets?: string[];
  hiddenValues?: Record<string, string>;
  describeItem: (item: any) => string;
  showPackYearCalculator?: boolean;
  api: {
    type: 'patientIssue' | 'history' | 'lifestyle' | 'consultant';
    field?: string;
  };
  schemaFields: string[];
  defaultValues: Record<string, any>;
}

export const SECTION_CONFIGS: Record<string, SectionConfig> = {
  allergies: {
    title: 'Allergy',
    formKey: 'allergies',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    presets: ['NKA', 'NKDA'],
    describeItem: (item: any) => item.value.name,
    api: { type: 'patientIssue', field: 'allergies' },
    schemaFields: ['name', 'notes'],
    defaultValues: { name: '', notes: '' },
  },
  active_issues: {
    title: 'Active Issues',
    formKey: 'active_issues',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    describeItem: (item: any) => item.value.name,
    api: { type: 'patientIssue', field: 'active_issues' },
    schemaFields: ['name', 'notes'],
    defaultValues: { name: '', notes: '' },
  },
  medical_issues: {
    title: 'Medical History',
    formKey: 'medical_issues',
    fields: [
      {
        name: 'issue_type',
        label: 'Issue Type',
        type: 'select',
        options: Object.values(ISSUE_TYPES),
        style: { flex: '0 0 150px' },
      },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    describeItem: (item: any) => item.value.name,
    api: { type: 'patientIssue', field: 'medical_issues' },
    schemaFields: ['name', 'notes', 'issue_type'],
    defaultValues: { name: '', notes: '', issue_type: '' },
  },
  preventative_screenings: {
    title: 'Preventive Screening',
    formKey: 'preventative_screenings',
    fields: [
      {
        name: 'name',
        label: 'Name',
        type: 'autocomplete',
        options: AUTOCOMPLETE_HISTORY.preventative,
        required: true,
      },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    describeItem: (item: any) => item.value.name,
    api: { type: 'patientIssue', field: 'preventative_screenings' },
    schemaFields: ['name', 'notes'],
    defaultValues: { name: '', notes: '' },
  },
  notes: {
    title: 'Notes',
    formKey: 'notes',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    describeItem: (item: any) => item.value.name,
    api: { type: 'patientIssue', field: 'notes' },
    schemaFields: ['name', 'notes'],
    defaultValues: { name: '', notes: '' },
  },
  family_history: {
    title: HISTORY_TYPES.family_history,
    formKey: 'history',
    fields: [
      {
        name: 'name',
        label: 'Relation',
        type: 'autocomplete',
        options: AUTOCOMPLETE_HISTORY.relation,
        required: true,
        style: { flex: '0 0 150px' },
      },
      { name: 'details', label: 'Details', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    hiddenValues: { history_type: HISTORY_TYPES.family_history },
    describeItem: (item: any) =>
      `${item.value.name}${item.value.details ? ' - ' + item.value.details : ''}`,
    api: { type: 'history' },
    schemaFields: ['name', 'details', 'notes', 'history_type'],
    defaultValues: { name: '', details: '', notes: '', history_type: HISTORY_TYPES.family_history },
  },
  surgical_history: {
    title: HISTORY_TYPES.surgical_history,
    formKey: 'history',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'details', label: 'Details', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    hiddenValues: { history_type: HISTORY_TYPES.surgical_history },
    describeItem: (item: any) =>
      `${item.value.name}${item.value.details ? ' - ' + item.value.details : ''}`,
    api: { type: 'history' },
    schemaFields: ['name', 'details', 'notes', 'history_type'],
    defaultValues: {
      name: '',
      details: '',
      notes: '',
      history_type: HISTORY_TYPES.surgical_history,
    },
  },
  ethnicity_history: {
    title: HISTORY_TYPES.ethnicity_history,
    formKey: 'history',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'details', label: 'Details', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    hiddenValues: { history_type: HISTORY_TYPES.ethnicity_history },
    describeItem: (item: any) =>
      `${item.value.name}${item.value.details ? ' - ' + item.value.details : ''}`,
    api: { type: 'history' },
    schemaFields: ['name', 'details', 'notes', 'history_type'],
    defaultValues: {
      name: '',
      details: '',
      notes: '',
      history_type: HISTORY_TYPES.ethnicity_history,
    },
  },
  lifestyle_factors: {
    title: 'Lifestyle Factor',
    formKey: 'lifestyle_factors',
    fields: [
      {
        name: 'name',
        label: 'Name',
        type: 'autocomplete',
        options: LIFESTYLE_FACTORS,
        required: true,
      },
      {
        name: 'details',
        label: 'Details',
        type: 'text',
        dynamicProps: (values) => ({
          disabled: values.name === 'Smoking',
          label: values.name === 'Smoking' ? 'Pack-year' : 'Details',
        }),
      },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    showPackYearCalculator: true,
    describeItem: (item: any) =>
      `${item.value.name}${item.value.details ? ' - ' + item.value.details : ''}`,
    api: { type: 'lifestyle' },
    schemaFields: ['name', 'details', 'notes'],
    defaultValues: { name: '', details: '', notes: '' },
  },
  consultants: {
    title: 'Consultant',
    formKey: 'consultants',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'specialty', label: 'Specialty', type: 'text', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
    describeItem: (item: any) =>
      `${item.value.name}${item.value.specialty ? ' - ' + item.value.specialty : ''}`,
    api: { type: 'consultant' },
    schemaFields: ['name', 'specialty', 'notes'],
    defaultValues: { name: '', specialty: '', notes: '' },
  },
};
